import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link, useNavigate } from 'react-router-dom';
import { aiVetAPI, petAPI } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import {
  ChatBubbleLeftRightIcon,
  PlusIcon,
  ClockIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

const AIVet = () => {
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [selectedPet, setSelectedPet] = useState('');
  const [chatTitle, setChatTitle] = useState('');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: sessions, isLoading: sessionsLoading } = useQuery(
    'aiVetSessions',
    aiVetAPI.getSessions
  );

  const { data: pets } = useQuery('pets', petAPI.getPets);

  const createSessionMutation = useMutation(aiVetAPI.createSession, {
    onSuccess: (data) => {
      queryClient.invalidateQueries('aiVetSessions');
      setShowNewChatModal(false);
      setSelectedPet('');
      setChatTitle('');
      navigate(`/app/virtual-vet/chat/${data.data.id}`);
    }
  });

  const deleteSessionMutation = useMutation(aiVetAPI.deleteSession, {
    onSuccess: () => {
      queryClient.invalidateQueries('aiVetSessions');
    }
  });

  const handleCreateSession = (e) => {
    e.preventDefault();
    if (!selectedPet) return;

    const petName = pets?.data?.find(p => p.id === parseInt(selectedPet))?.name;
    const title = chatTitle.trim() || `Chat about ${petName}`;

    createSessionMutation.mutate({
      pet_id: parseInt(selectedPet),
      title
    });
  };

  const handleDeleteSession = (sessionId) => {
    if (window.confirm('Are you sure you want to delete this chat session?')) {
      deleteSessionMutation.mutate(sessionId);
    }
  };

  if (sessionsLoading) {
    return <LoadingSpinner text="Loading AI Vet..." />;
  }

  const sessionsData = sessions?.data || [];
  const petsData = pets?.data || [];

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <SparklesIcon className="w-8 h-8 text-primary-600 mr-2" />
            AI Veterinary Assistant
          </h1>
          <p className="text-gray-600">Chat with our AI assistant for pet health guidance and advice</p>
        </div>
        <button
          onClick={() => setShowNewChatModal(true)}
          className="btn btn-primary flex items-center"
          disabled={petsData.length === 0}
        >
          <PlusIcon className="w-4 h-4 mr-2" />
          New Chat
        </button>
      </div>

      {/* Disclaimer */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex">
          <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-yellow-800">Important Disclaimer</h3>
            <p className="mt-1 text-sm text-yellow-700">
              This AI assistant provides general information and guidance only. It is not a substitute for professional veterinary care. 
              For emergencies or serious health concerns, please contact your veterinarian immediately.
            </p>
          </div>
        </div>
      </div>

      {petsData.length === 0 ? (
        <div className="text-center py-12">
          <ChatBubbleLeftRightIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No pets registered</h3>
          <p className="mt-1 text-sm text-gray-500">
            Add a pet to your profile to start chatting with the AI vet.
          </p>
          <div className="mt-6">
            <Link to="/app/pets" className="btn btn-primary">
              Add Pet
            </Link>
          </div>
        </div>
      ) : (
        <>
          {/* Recent Chat Sessions */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-gray-900">Recent Chats</h2>
              {sessionsData.length > 0 && (
                <Link to="/app/virtual-vet/sessions" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
                  View All
                </Link>
              )}
            </div>

            {sessionsData.length === 0 ? (
              <div className="text-center py-8">
                <ChatBubbleLeftRightIcon className="mx-auto h-10 w-10 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No chat sessions yet</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Start your first conversation with the AI vet assistant.
                </p>
                <div className="mt-4">
                  <button
                    onClick={() => setShowNewChatModal(true)}
                    className="btn btn-primary"
                  >
                    Start First Chat
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {sessionsData.slice(0, 5).map((session) => (
                  <div key={session.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex items-center space-x-3 flex-1">
                      <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                        <ChatBubbleLeftRightIcon className="w-5 h-5 text-primary-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{session.title}</h3>
                        <div className="flex items-center space-x-2 text-sm text-gray-500">
                          <span>{session.pet_name} ({session.pet_type})</span>
                          <span>•</span>
                          <span>{session.message_count} messages</span>
                          {session.last_message_at && (
                            <>
                              <span>•</span>
                              <span>{new Date(session.last_message_at).toLocaleDateString()}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Link
                        to={`/app/virtual-vet/chat/${session.id}`}
                        className="px-3 py-1 bg-primary-600 text-white rounded text-sm font-medium hover:bg-primary-700"
                      >
                        Continue
                      </Link>
                      <button
                        onClick={() => handleDeleteSession(session.id)}
                        className="p-1 text-red-600 hover:text-red-700"
                        title="Delete session"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="card">
            <h2 className="text-lg font-medium text-gray-900 mb-4">How the AI Vet Can Help</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="p-4 border border-gray-200 rounded-lg">
                <ChatBubbleLeftRightIcon className="w-8 h-8 text-blue-600 mb-2" />
                <h3 className="font-medium text-gray-900">Health Questions</h3>
                <p className="text-sm text-gray-500">Ask about symptoms, behaviors, or general health concerns</p>
              </div>
              
              <div className="p-4 border border-gray-200 rounded-lg">
                <ClockIcon className="w-8 h-8 text-green-600 mb-2" />
                <h3 className="font-medium text-gray-900">Care Guidance</h3>
                <p className="text-sm text-gray-500">Get advice on feeding, exercise, and daily care routines</p>
              </div>

              <div className="p-4 border border-gray-200 rounded-lg">
                <SparklesIcon className="w-8 h-8 text-purple-600 mb-2" />
                <h3 className="font-medium text-gray-900">Behavior Help</h3>
                <p className="text-sm text-gray-500">Understand your pet's behavior and training needs</p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* New Chat Modal */}
      {showNewChatModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Start New Chat</h3>
            <form onSubmit={handleCreateSession} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Pet
                </label>
                <select
                  value={selectedPet}
                  onChange={(e) => setSelectedPet(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                >
                  <option value="">Choose a pet...</option>
                  {petsData.map((pet) => (
                    <option key={pet.id} value={pet.id}>
                      {pet.name} ({pet.pet_type})
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Chat Title (Optional)
                </label>
                <input
                  type="text"
                  value={chatTitle}
                  onChange={(e) => setChatTitle(e.target.value)}
                  placeholder="e.g., Check-up questions for Max"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowNewChatModal(false);
                    setSelectedPet('');
                    setChatTitle('');
                  }}
                  className="flex-1 py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!selectedPet || createSessionMutation.isLoading}
                  className="flex-1 py-2 px-4 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
                >
                  {createSessionMutation.isLoading ? 'Creating...' : 'Start Chat'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIVet;